// react
import { memo, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
// configs
import { EX5250, EX6381, EX6778 } from 'config/flags';
// components
import {
    CredentialError,
    CreditError,
    DefaultWithoutError,
    ExpiredPasswordError,
    MissingAccountError,
    UnknownError,
    MissingDispatchAddressError,
    MissingFormatError,
} from '../TableHeaderErrors/';
import { TableHeadDropdownMenu } from '../TableHeadDropdownMenu';
import InsufficientCreditWarning from '../TableHeaderWarnings/InsufficientCreditWarning/InsufficientCreditWarning';
// context
import { useCredentialErrors } from 'context/CredentialsErrors/CredentialsErrors';
import { useDrugManufacturersContext, useFetchingContext, useInsuficientCreditContext } from 'context';
// utils
import { addEllipsis } from 'utils/strings/ellipsis.utils';
import { handlerWidthColumns, checkErrors } from '../utils';
import { switchMessageErrors } from '../utils/errorMessages';
import useErrorsObjects from 'utils/hooks/useErrorsObjects/useErrorsObjects';
import { EErrorCode } from 'utils/enums/comparativeAlerts/comparativeAlerts';
import { EDrugManufacturerTypeId } from 'utils/enums/drugmanufacturerTypeId/DrugManufacturerTypeId';
import { useUser } from 'utils/hooks';
import { useCDOStatus } from '@commons/modules/TableProductComparison/hooks';
import { cellEvents } from 'utils/cellEvents';
// styles
import styles from './CellDrugManufacturerHeader.module.css';
import { getDrugManufacturerByIdWithCredentials } from 'api/drugManufacturers';
import { useModalCredentialsContext } from 'context/ModalCredentials/ModalCredentials-context';
import { typesOfErrors, typesOfErrorsMultiple } from 'utils/drugManufacturers/errors';
import { handleSwitch } from '@commons/modules/InitialConfig/PointOfSales/NewCredentialCard/NewCredentialCards.helpers';
import { validateTypeConnection } from 'utils/drugManufacturers/validateTypeConnection';
import { useToggleSwitch } from 'context/toggleSwitch/ToggleSwitchContext';
import { t } from 'i18next';
import { useProductComparison } from 'context/product-comparison.context';

const CellDrugManufacturerHeader = ({ item, idx, parentHeaderRef, fetchProductGroup }) => {
    const { user } = useUser({});
    const EX5506 = user?.EX5506; //FF epic insufficientCredit V2
    const EX6063 = user?.EX6063;
    // ref
    const refCell = useRef(null);
    const EX6017 = user?.EX6017;

    const { CDO_FF, IS_ARG } = useCDOStatus();

    const isVendor = EX6381
        ? !item?.vendor_accepted && item?.type_id === EDrugManufacturerTypeId.vendor
        : EX6017 && item?.type_id === EDrugManufacturerTypeId.vendor;

    // context
    const { extractedDataError, isFetchingUpdate } = useCredentialErrors();

    const { loading } = useFetchingContext();

    const { insufficientCreditIDs } = useInsuficientCreditContext();

    const { drugManufacturer, isEnabledDrugManufacturer } = useDrugManufacturersContext();

    const {
        handlerDrugConnection,
        handlerVendorConnection,
        initializeForm,
        setErrorMessage,
        setFormError,
        toggleModal,
        setDrugManufacturers,
    } = useModalCredentialsContext();

    const { toggleSwitch } = useToggleSwitch();

    const isEnabled = isEnabledDrugManufacturer(item.id);

    const [selectAll, setSelectAll] = useState(false);

    const { query } = useRouter();

    const event = cellEvents('rest', item, selectAll, setSelectAll, isEnabled, item.id, query, fetchProductGroup);

    const hasInsufficientCredit = EX5506 && insufficientCreditIDs.some((creditID) => creditID.id === item.id);
    // hook
    const errors = useErrorsObjects(extractedDataError);
    // states
    const [errorsTypes, setErrorsTypes] = useState({
        credentials: false,
        credit: false,
        unknown: false,
        expiredPassword: false,
        missingAccount: false,
        missingDispatchAddress: false,
        missingFormat: false,
    });
    // logic
    const setterWidthInDocument = () => {
        return handlerWidthColumns(
            CDO_FF,
            IS_ARG,
            drugManufacturer,
            `${refCell?.current?.getBoundingClientRect()?.width}px`,
        );
    };
    const messageOfErrors = switchMessageErrors(extractedDataError, extractedDataError?.length);
    const truncateText: any = addEllipsis(item.name);

    // init
    useEffect(() => {
        const handleResize = () => {
            setterWidthInDocument();
        };

        // Llamar a la función inicialmente
        handleResize();

        // Agregar el evento resize
        window.addEventListener('resize', handleResize);

        return () => {
            // Limpiar el evento resize
            window.removeEventListener('resize', handleResize);
        };
    }, [refCell?.current?.getBoundingClientRect()?.width]);
    // END LOGIC WIDTH CDO, CELLS AND FIRST COLUMN

    useEffect(() => {
        const errors = checkErrors(extractedDataError, item.id);

        setErrorsTypes({
            credentials: errors.hasErrorCredential,
            credit: errors.hasErrorCredit,
            unknown: errors.hasUnknownError,
            expiredPassword: errors.hasErrorExpiredPassword,
            missingAccount: errors.hasErrorMissingAccount,
            missingDispatchAddress: errors.hasErrorMissingDispatchAddress,
            missingFormat: errors.hasIncorrectFormatFile,
        });
    }, [extractedDataError, item.id, isFetchingUpdate]);

    const creditError = errors[EErrorCode.CREDIT_LIMIT_REACHED];
    const wrongPassError = errors[EErrorCode.WRONG_USERNAME_PASSWORD];
    const expiredPassError = errors[EErrorCode.EXPIRED_PASSWORD];
    const missingAccountError = errors[EErrorCode.MISSING_ACCOUNT];
    const missingDispatchAddressError = errors[EErrorCode.MISSING_DISPATCH_ADDRESS];
    const missingFormat = errors[EErrorCode.MISSING_FORMAT];

    const disabledButtonWithError =
        !!errorsTypes.credentials ||
        !!errorsTypes.credit ||
        !!errorsTypes.expiredPassword ||
        !!errorsTypes.missingAccount ||
        !!errorsTypes.missingDispatchAddress ||
        !!errorsTypes.missingFormat ||
        !!errorsTypes.unknown ||
        isVendor;

        const isTheLast = idx === drugManufacturer.length - 1;
        const isPenultimate = idx === drugManufacturer.length - 2;

    /* const kellerhoffIds = [6, 13, 14, 15, 16, 17, 22, 23];

    const firstKellerhoffElem = drugManufacturer.find(item => kellerhoffIds.includes(item.id)); */
    
    const manufacturerInfo = {
        id: item.id,
        credit: item.credit_available,
        name: item.name,
    };


    // FN encargada de ejectuar el modal de config
    const triggerModal = (drug, id, credential_types, typeId, isUnknownCredential, isErrorCredential) => {
        const isEditing = credential_types?.some((credential) => credential.api_credential_id !== null);
        const credentialReady = credential_types?.filter((credential) => credential.api_credential_id !== null);

        const isMultipleCredential = credential_types?.length > 1;
        const errorsMultiples = isMultipleCredential ? typesOfErrorsMultiple(drug) : null;

        let credentialType = handleSwitch(credentialReady[0]?.name, credentialReady); // Default to the first credential

        if (isMultipleCredential) {
            const { hasSomeError_first, hasSomeError_second } = errorsMultiples || {};

            if (hasSomeError_first) {
                credentialType = handleSwitch(credentialReady[0]?.name, credentialReady);
            } else if (hasSomeError_second) {
                credentialType = handleSwitch(credentialReady[0]?.name, credentialReady);
            }
        }

        // common function
        const openCredentialModal = () => {
            if (typeId === 3) {
                handlerVendorConnection(id);
            } else {
                const parsedType = validateTypeConnection(credentialType);
                handlerDrugConnection('', id, credential_types, parsedType);
                toggleModal.toggle();
            }
        };

        if (isUnknownCredential) {
            toggleSwitch();
            if (isEditing) {
                initializeForm(credential_types, id, credentialReady[0]?.name);
            }
            openCredentialModal();
        } else if (isErrorCredential) {
            if (isEditing) {
                setFormError({ username: true, password: true });
                setErrorMessage({
                    status: 400,
                    msgError: t('efficientPurchasing.credentialsError.panelErrorUserOrPassword'),
                });
                initializeForm(credential_types, id, credentialReady[0]?.name);
            }
            openCredentialModal();
        } else {
            if (isEditing) {
                initializeForm(credential_types, id, credentialReady[0]?.name);
            }
            openCredentialModal();
        }
    };

    const handleOpenConnectionModal = async (): Promise<void> => {
        if (EX6778) {
            const response = await getDrugManufacturerByIdWithCredentials(query.posId, item.id);
            if (response?.length) {
                const drug = response[0];
                const isErrorCredential = typesOfErrors(drug)?.has_wrong_username_password;
                setDrugManufacturers({ data: response });
                triggerModal(drug, drug.id, drug.credential_types, drug.type.id, missingFormat, isErrorCredential);
            } else {
                console.error(`Algo salio mal al obtener datos de la droguería ${item.id}`);
            }
        }
    };

    return (
        <div
            className={styles['cell-price']}
            id={`drugManufacturer-${idx}`}
            ref={refCell}
            style={{ backgroundColor: !isEnabled ? '#F5F6FA' : '' }}
        >
            {/* case default */}
            {!errorsTypes.credentials &&
                !errorsTypes.credit &&
                !hasInsufficientCredit &&
                !errorsTypes.unknown &&
                !errorsTypes.expiredPassword &&
                !errorsTypes.missingAccount &&
                !errorsTypes.missingDispatchAddress &&
                !errorsTypes.missingFormat && (
                    <DefaultWithoutError
                        disabled={!truncateText.hasEllipsis}
                        name={truncateText.texts}
                        textTooltip={truncateText.texts}
                        flag="rest"
                        isEnabled={isEnabled}
                        delivery={item?.delivery}
                        offline={item?.offline}
                    />
                )}
            {/* case credential error */}
            {errorsTypes.credentials && (
                <CredentialError
                    truncateText={truncateText}
                    title={
                        !!EX5250
                            ? wrongPassError.tooltip.title
                            : messageOfErrors.wrongUserPass.errorAlertWrongUserPassword.titleTooltip
                    }
                    subtitle={
                        !!EX5250
                            ? wrongPassError.tooltip.subtitle
                            : messageOfErrors?.wrongUserPass?.errorAlertWrongUserPassword?.subtextTooltip
                    }
                    haveErrorCredentials={errorsTypes.credentials}
                    placement={isTheLast ? 'bottomEnd' : 'bottom'}
                    handleOpenConnectionModal={handleOpenConnectionModal}
                />
            )}
            {/* case warning insufficient credit v2 */}
            {hasInsufficientCredit && (
                <InsufficientCreditWarning
                    truncateText={truncateText}
                    manufacturerInfo={manufacturerInfo}
                    placement={isTheLast || isPenultimate ? 'bottomEnd' : 'bottomStart'}
                    isEnabled={isEnabled}
                    fetchProductGroup={fetchProductGroup}
                />
            )}
            {/* case credit */}
            {errorsTypes.credit && (
                <CreditError
                    truncateText={truncateText}
                    title={
                        !!EX5250
                            ? creditError?.tooltip?.title
                            : messageOfErrors?.creditLimit?.errorAlertCreditLimit?.titleTooltip
                    }
                    subtitle={
                        !!EX5250
                            ? creditError?.tooltip?.subtitle
                            : messageOfErrors?.creditLimit?.errorAlertCreditLimit?.subtextTooltip
                    }
                    haveErrorCredit={errorsTypes.credit}
                    placement={isTheLast ? 'bottomEnd' : 'bottom'}
                />
            )}
            {/* case expired password */}
            {errorsTypes.expiredPassword && (
                <ExpiredPasswordError
                    truncateText={truncateText}
                    haveExpiredPasswordError={errorsTypes.expiredPassword}
                    title={!!EX5250 && expiredPassError?.tooltip.title}
                    subtitle={!!EX5250 && expiredPassError?.tooltip.subtitle}
                    placement={isTheLast ? 'bottomEnd' : 'bottom'}
                    handleOpenConnectionModal={handleOpenConnectionModal}
                />
            )}
            {/* case missing account */}
            {errorsTypes.missingAccount && (
                <MissingAccountError
                    haveMissingAccountError={errorsTypes.missingAccount}
                    title={!!EX5250 && missingAccountError?.tooltip.title}
                    subtitle={!!EX5250 && missingAccountError?.tooltip.subtitle}
                    truncateText={truncateText}
                    placement={isTheLast ? 'bottomEnd' : 'bottom'}
                    handleOpenConnectionModal={handleOpenConnectionModal}
                />
            )}
            {/* case missing dispatch address */}
            {errorsTypes.missingDispatchAddress && (
                <MissingDispatchAddressError
                    haveMissingDispatchAddress={errorsTypes.missingDispatchAddress}
                    title={!!EX5250 && missingDispatchAddressError?.tooltip.title}
                    subtitle={!!EX5250 && missingDispatchAddressError?.tooltip.subtitle}
                    truncateText={truncateText}
                    placement={isTheLast ? 'bottomEnd' : 'bottom'}
                    handleOpenConnectionModal={handleOpenConnectionModal}
                />
            )}
            {/*case missing format file*/}
            {EX6063 && errorsTypes.missingFormat && (
                <MissingFormatError
                    truncateText={truncateText}
                    title={missingFormat.tooltip.title}
                    subtitle={missingFormat.tooltip.subtitle}
                    haveIncorrectFormat={true}
                    placement={isTheLast ? 'bottomEnd' : 'bottom'}
                    handleOpenConnectionModal={handleOpenConnectionModal}
                />
            )}
            {/* case unknown error */}
            {errorsTypes.unknown && <UnknownError truncateText={truncateText} />}

            <TableHeadDropdownMenu
                isEnabled={isEnabled}
                item={item}
                flag="rest"
                isActive={null}
                isDefault={disabledButtonWithError || !item.has_stock || !!item.last_available}
                parentHeaderRef={parentHeaderRef}
                loading={loading}
                event={event}
                selectAll={selectAll}
                setSelectAll={setSelectAll}
                fetchProductGroup={fetchProductGroup}
            />
            {/* dropdown menu */}
        </div>
    );
};

export default memo(CellDrugManufacturerHeader);
